import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import { theme } from '@/core/theme';
import { computeText } from '@/locales/utils';
import RadioButton from '@/components/atoms/RadioButton';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { ChannelId } from '@/services/innovorder/channel/channel.type';
import { Input } from '@/components/form/Input';
import { useSelector } from 'react-redux';
import { useGetLocationsQuery } from '@/services/innovorder/marketplace/marketplace.endpoints';
import { getUserToken } from '@/redux/user';
import { useParams } from 'react-router';
import { Menu } from '@/services/innovorder/menu/menu.type';
import { MenuModalChannelsTitle } from '../../MenuModal.style';
import { EditChannelFormValues } from '../CreateMenuModal.type';
import {
    ModalButton,
    ModalButtonLabel,
    ModalImageInput,
    ModalInputContainer,
    ModalInputTitle,
    ModalInputWrapper,
    ModalPosButton,
    ModalTextInput,
} from '../CreateMenuModal.style';

type EditChannelStepProps = {
    onNext?: () => void;
    openDeliverooModal?: () => void;
    openUbereatsModal?: () => void;
    restaurantId?: number;
    isLoading?: boolean;
    hasExternalIdMapping?: boolean;
    menu?: Menu;
};

export const EditChannelStep: React.FunctionComponent<React.PropsWithChildren<EditChannelStepProps>> = ({
    onNext,
    openDeliverooModal,
    openUbereatsModal,
    restaurantId,
    isLoading = false,
    hasExternalIdMapping = false,
    menu,
}) => {
    const token = useSelector(getUserToken);
    const { brandId } = useParams<{ brandId: string; restaurantId: string }>();
    const intl = useIntl();
    const { control } = useFormContext<EditChannelFormValues>();
    const { data: deliverooLocations } = useGetLocationsQuery({
        brandId,
        restaurantId: restaurantId?.toString() ?? '',
        token,
        marketplace: 'deliveroo',
    });

    const { data: ubereatsLocations } = useGetLocationsQuery({
        brandId,
        restaurantId: restaurantId?.toString() ?? '',
        token: token,
        marketplace: 'ubereats',
    });
    const shouldDisplayDeliverooButton = useMemo(() => {
        return deliverooLocations && deliverooLocations?.length > 0;
    }, [deliverooLocations]);

    const shouldDisplayUberEatsButton = useMemo(() => {
        return ubereatsLocations && ubereatsLocations?.length > 0;
    }, [ubereatsLocations]);

    return (
        <>
            <Controller
                control={control}
                name="name"
                rules={{
                    required: computeText(intl, 'field.required'),
                    validate: (value = '') => !!value?.length,
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <ModalInputContainer>
                        <ModalInputWrapper>
                            <ModalInputTitle type="small" weight="medium" text="menu.modal.menu.name" />
                            <ModalTextInput
                                type="string"
                                data-testid="menu-modal-menu-name-input"
                                error={error?.message}
                                onChange={onChange}
                                value={value?.toString()}
                                disabled={isLoading}
                            />
                        </ModalInputWrapper>
                        <ModalImageInput
                            name="imageId"
                            {...(restaurantId ? { restaurantId } : { brandId: Number(brandId) })}
                            {...(menu?.image && {
                                defaultImage: { imageId: menu.image.imageId, fullUrl: menu.image.fullUrl },
                            })}
                        />
                    </ModalInputContainer>
                )}
            />
            {restaurantId && (
                <Controller
                    control={control}
                    name="channels"
                    render={({ field: { onChange, value } }) => {
                        function onChannelChange(target: ChannelId.WEB | ChannelId.KIOSK) {
                            const updatedValues = value.filter(({ channelId }) => channelId !== target);

                            if (updatedValues.length === value.length) {
                                updatedValues.push({ channelId: target });
                            }

                            onChange(updatedValues);
                        }

                        return (
                            <div>
                                <MenuModalChannelsTitle
                                    type="small"
                                    weight="medium"
                                    text="menu.modal.channels.selectActive"
                                />
                                <ModalButton disabled={isLoading}>
                                    <RadioButton
                                        data-testid="menu-modal-channels-web-radio"
                                        label="legend.WEB"
                                        onChange={onChannelChange}
                                        checked={value.some(({ channelId }) => channelId === ChannelId.WEB)}
                                        value={ChannelId.WEB}
                                        disabled={isLoading}
                                    />
                                </ModalButton>
                                <ModalButton disabled={isLoading}>
                                    <RadioButton
                                        data-testid="menu-modal-channels-kiosk-radio"
                                        label="legend.KIOSK"
                                        onChange={onChannelChange}
                                        checked={value.some(({ channelId }) => channelId === ChannelId.KIOSK)}
                                        value={ChannelId.KIOSK}
                                        disabled={isLoading}
                                    />
                                </ModalButton>
                                <ModalPosButton
                                    data-testid="menu-modal-channels-pos-radio"
                                    onClick={onNext}
                                    disabled={isLoading}
                                >
                                    <SVGIcon size={18} icon={SVGIcons.POS} color={theme.color.black200} />
                                    <ModalButtonLabel
                                        type="normal"
                                        text="menu.modal.channels.associatePos"
                                    ></ModalButtonLabel>
                                    <SVGIcon icon={SVGIcons.ARROW_FORWARD} />
                                </ModalPosButton>
                                {shouldDisplayDeliverooButton && (
                                    <ModalPosButton
                                        data-testid="menu-modal-channels-deliveroo-radio"
                                        onClick={openDeliverooModal}
                                        disabled={isLoading}
                                    >
                                        <SVGIcon size={18} icon={SVGIcons.DELIVEROO} color={theme.color.black200} />
                                        <ModalButtonLabel
                                            type="normal"
                                            text="menu.modal.channels.associateDeliveroo"
                                        ></ModalButtonLabel>
                                        <SVGIcon icon={SVGIcons.ARROW_FORWARD} />
                                    </ModalPosButton>
                                )}
                                {shouldDisplayUberEatsButton && (
                                    <ModalPosButton
                                        data-testid="menu-modal-channels-ubereats-radio"
                                        onClick={openUbereatsModal}
                                        disabled={isLoading}
                                    >
                                        <SVGIcon size={18} icon={SVGIcons.UBEREATS} color={theme.color.black200} />
                                        <ModalButtonLabel
                                            type="normal"
                                            text="menu.modal.channels.associateUberEats"
                                        ></ModalButtonLabel>
                                        <SVGIcon icon={SVGIcons.ARROW_FORWARD} />
                                    </ModalPosButton>
                                )}
                            </div>
                        );
                    }}
                />
            )}
            {hasExternalIdMapping && <Input labelId="menu.modal.externalId" name="externalId" />}
        </>
    );
};
